import type { Chain } from './constants';

/**
 * URL-friendly chain slug mappings.
 *
 * The slug appears in the URL path (e.g. /is/valikoimat, /bs/valikoimat).
 * It maps to the full chain identifier used internally.
 */
export const CHAIN_SLUGS = {
  is: 'intersport',
  bs: 'budgetsport',
} as const;

export type ChainSlug = keyof typeof CHAIN_SLUGS;

export const CHAIN_ID_TO_SLUG: Record<Chain, ChainSlug> = {
  intersport: 'is',
  budgetsport: 'bs',
};

/**
 * Services whose routes are chain-dependent and appear under /[chain]/...
 */
export const CHAIN_DEPENDENT_BASE_PATHS = [
  '/valikoimat',
  '/taydennystilaukset',
  '/tyosuhdepyorat',
  '/kuvahaku',
  '/asetukset',
] as const;

export function chainSlugToId(slug: string): Chain | undefined {
  return CHAIN_SLUGS[slug as ChainSlug] as Chain | undefined;
}

export function chainIdToSlug(chain: Chain): ChainSlug;
export function chainIdToSlug(chain: string): ChainSlug | undefined;
export function chainIdToSlug(chain: string): ChainSlug | undefined {
  return CHAIN_ID_TO_SLUG[chain as Chain];
}

export function isValidChainSlug(slug: string): slug is ChainSlug {
  return slug in CHAIN_SLUGS;
}

/**
 * Build a chain-prefixed URL path.
 *
 * @example buildChainUrl('/valikoimat/tilaukset', 'is') => '/is/valikoimat/tilaukset'
 */
export function buildChainUrl(path: string, chainSlug: ChainSlug): string {
  return `/${chainSlug}${path}`;
}

/**
 * Extract the chain slug from a pathname, if present.
 *
 * @example extractChainFromPath('/is/valikoimat/tilaukset') => 'is'
 * @example extractChainFromPath('/hallinta') => undefined
 */
export function extractChainFromPath(pathname: string): ChainSlug | undefined {
  const firstSegment = pathname.split('/')[1];
  return isValidChainSlug(firstSegment ?? '') ? (firstSegment as ChainSlug) : undefined;
}

/**
 * Strip the chain slug from a pathname, returning the path without chain prefix.
 *
 * @example stripChainFromPath('/is/valikoimat/tilaukset') => '/valikoimat/tilaukset'
 * @example stripChainFromPath('/hallinta') => '/hallinta'
 */
export function stripChainFromPath(pathname: string): string {
  const firstSegment = pathname.split('/')[1];
  if (isValidChainSlug(firstSegment ?? '')) {
    return pathname.slice(firstSegment!.length + 1) || '/';
  }
  return pathname;
}

/**
 * Read the chain from the `ifi-chain` cookie (client-side only).
 * Returns the full chain ID (e.g. 'intersport') or undefined.
 */
export function getChainFromCookie(): Chain | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = /(?:^|;\s*)ifi-chain=([^;]*)/.exec(document.cookie);
  return match?.[1] ? chainSlugToId(match[1]) : undefined;
}

/**
 * Check if a path (without chain prefix) is a chain-dependent route.
 */
export function isChainDependentPath(pathname: string): boolean {
  const stripped = stripChainFromPath(pathname);
  return CHAIN_DEPENDENT_BASE_PATHS.some((base) => stripped.startsWith(base));
}
